import { Translations } from './translations.d';

export const translations: Translations = {
  es: {
    translation: {
      nav: {
        home: 'Inicio',
        customers: 'Clientes',
        platform: 'Plataforma',
        autonomous: 'Agentes Autónomos',
        connectors: 'Conectores',
        demo: 'Demo',
        security: 'Seguridad',
        partners: 'Partners',
        pricing: 'Precios',
      },
      tagline: {
        differentiator: 'WAI no es una plataforma de IA. Es la plataforma empresarial donde la IA es el motor.',
        short: 'Plataforma empresarial. Motor IA.',
      },
      hero: {
        title: 'Working AI',
        subtitle: 'WAI no es una plataforma de IA. Es la plataforma empresarial donde la IA es el motor.',
        description: 'Conectamos tus datos donde ya están, los entendemos con IA, y construimos las aplicaciones de negocio, modelos predictivos y agentes — autónomos incluidos — que tu empresa necesita. Y los desplegamos en el mismo producto.',
        primaryCta: 'Ver demo en vivo',
        secondaryCta: 'Ver plataforma',
        pillarConnect: 'Conecta',
        pillarUnderstand: 'Entiende',
        pillarBuild: 'Construye',
        pillarDeploy: 'Despliega',
        imageAlt: 'Dashboard de salud operativa generado por WAI, con KPIs en vivo sobre datos empresariales',
      },
      customers: {
        eyebrow: 'Probado en producción',
        number: '+25',
        title: 'empresas ya operan con WAI a diario',
        subtitle: 'Cada una opera con WAI como su motor empresarial —no como una herramienta más de IA—, sobre sus propios datos. Procesos críticos, decisiones reales, en producción.',
        credibility: ['En producción real', 'Multi-sector', 'Multi-país', 'Entornos regulados'],
        reinforcement: 'Porque WAI no es una plataforma de IA: es la plataforma empresarial donde la IA es el motor.',
      },
      oneplatform: {
        title: 'Una plataforma. De tus datos a aplicaciones IA para negocio, en vivo.',
        subtitle: 'Cuatro pasos. Un solo producto. Seguridad y gobierno en cada capa.',
        closing: 'Mismo producto, mismo flujo — sin saltar a otra herramienta.',
        steps: [
          {
            title: 'Conecta',
            description: 'A tus datos donde ya están. Sin copias innecesarias, sin movimientos forzados.',
          },
          {
            title: 'Entiende',
            description: 'WAI lee tus datos y aprende qué significan. Sin diccionario manual.',
          },
          {
            title: 'Construye',
            description: 'Aplicaciones de negocio, modelos predictivos y agentes a medida — incluidos los autónomos.',
          },
          {
            title: 'Despliega',
            description: 'Un clic y a producción. Mismo producto, URL pública, versionado.',
          },
        ],
      },
      connect: {
        eyebrow: '01 · Conecta',
        title: 'Conecta donde ya están tus datos',
        subtitle: 'WAI se conecta in-place a tus sistemas empresariales. Tú decides qué fuentes y qué tablas; nosotros respetamos tus permisos y trabajamos en sólo lectura por defecto.',
        tagline: 'Tus datos donde ya están — WAI conecta in-place, sin copias innecesarias.',
        bullets: [
          {
            title: 'Catálogo curado de conectores',
            description: 'Más de 50 conectores empresariales listos: ERPs, CRMs, data warehouses, ficheros y APIs. Tus fuentes, no las nuestras.',
          },
          {
            title: 'Entorno dedicado por cliente',
            description: 'Cada cliente tiene su propia caja: datos, secretos y red aislados, fuera de cualquier otro tenant.',
          },
          {
            title: 'Sólo lectura por defecto',
            description: 'Sin caminos de escritura abiertos. Los privilegios bajan en cada salto: lo que llega al modelo es lo justo.',
          },
        ],
        imageCaption: 'Asistente de nueva conexión con catálogo de fuentes empresariales.',
        imageAlt: 'Asistente de WAI para crear una conexión de datos in-place a sistemas empresariales',
      },
      understand: {
        eyebrow: '02 · Entiende',
        title: 'WAI lee tus datos y aprende qué significan',
        subtitle: 'Sin diccionario manual. Sin proyecto paralelo de gobernanza. La capa de conocimiento se construye sola mientras conectas tus fuentes.',
        tagline: 'Tu capa de conocimiento, automática.',
        bullets: [
          {
            title: 'Tablas, columnas y documentos juntos',
            description: 'WAI une datos estructurados con documentación interna — todo se vuelve buscable y comprensible por la IA.',
          },
          {
            title: 'Indexado y gobernado por cliente',
            description: 'Tu conocimiento vive en tu tenant. Nunca se mezcla con el de otra empresa.',
          },
          {
            title: 'Detección de huecos',
            description: 'WAI te avisa cuando le falta contexto: columnas sin descripción, valores inesperados, relaciones por aprender.',
          },
        ],
        imageCaption: 'Vista de conocimiento disponible: nombres semánticos, clasificación de datos y huecos detectados.',
        imageAlt: 'Capa de conocimiento de WAI: tablas, columnas y documentos clasificados con nombres semánticos',
      },
      build: {
        eyebrow: '03 · Construye',
        title: 'WAI construye contigo: apps de negocio, modelos y agentes',
        subtitle: 'Una capa de creación, tres tipos de entregables. Todos versionados, todos auditados, todos desplegados dentro de WAI.',
        footnote: 'Y todo se despliega en el mismo producto — ver Despliegue ↓',
        apps: {
          title: 'Aplicaciones de negocio a medida',
          description: 'Cualquier aplicación de negocio sobre tus datos: dashboards, herramientas operativas, frontends de proceso, portales internos o paneles de gestión.',
          bullets: [
            'Cualquier aplicación de negocio, no sólo analíticas',
            'Biblioteca de bloques reutilizables',
            'Verificadas: compilan a la primera',
          ],
          alt: 'Aplicación de negocio generada por WAI sobre datos del cliente, con UI y lógica a medida',
        },
        models: {
          title: 'Modelos predictivos',
          description: 'Clasificación, regresión y forecast. Sin gestionar infraestructura.',
          bullets: [
            'WAI elige el patrón correcto',
            'Métricas reales sobre test',
            'Versionado con Live · Current · rollback',
          ],
          alt: 'Modelo predictivo de WAI con métricas reales sobre conjunto de test',
        },
        agents: {
          title: 'Agentes a medida',
          description: 'Define la persona, las skills y los permisos. WAI hace el resto.',
          bullets: [
            'System prompt, instrucciones y MCP Servers',
            'Permisos tool-by-tool: allow · deny · ask',
            'Versionado e historial completo',
          ],
          alt: 'Definición de un agente WAI: persona, instrucciones y permisos por herramienta',
        },
      },
      deployment: {
        eyebrow: '04 · Despliega',
        title: 'Un clic. URL pública. Listo.',
        subtitle: 'Tus apps de negocio, modelos y agentes salen a producción dentro del mismo WAI — sin equipo DevOps separado y sin segunda plataforma.',
        sameProductTagline: 'WAI no se detiene en generar: lo despliega.',
        chips: [
          'Listo para producción',
          'HTTPS automático',
          'Versionado con rollback',
        ],
        flow: [
          'Construye en WAI',
          'Despliegue automático',
          'URL pública con HTTPS',
        ],
        browser: {
          url: 'app.tu-tenant.wai.run',
          statusLabel: 'Estado',
          statusValue: 'Live',
          versionLabel: 'Versión',
          versionValue: 'v1.4 · publicada hace 2 min',
          footerChips: ['HTTPS', 'Versionado', 'Rollback en 1 clic'],
        },
      },
      autonomous: {
        eyebrow: 'Capacidad diferenciadora',
        heroTitle: 'Agentes que siguen trabajando cuando tú cierras el portátil.',
        heroSubtitle: 'Vigilan tu negocio 24/7, detectan eventos, deciden y actúan — con respuestas estructuradas y auditadas. Desplegados como el resto de tu WAI.',
        bullets: [
          {
            title: 'Vigilancia continua 24/7',
            description: 'Sin presencia humana. WAI revisa tus señales con la cadencia que tú definas.',
          },
          {
            title: 'Respuesta estructurada',
            description: 'Cada acción del agente queda registrada con datos, métricas y razonamiento.',
          },
          {
            title: 'Hiberna cuando no hay actividad',
            description: 'Sólo consume cuando hay algo que procesar. Sin coste fijo silencioso.',
          },
          {
            title: 'Identidad propia por despliegue',
            description: 'Cada agente actúa con su propia identidad y permisos. Sin atajos, sin secretos compartidos.',
          },
        ],
        cycleTitle: 'El ciclo autónomo',
        cycleSteps: [
          { title: 'Vigila', description: 'Escucha tus señales' },
          { title: 'Detecta', description: 'Identifica el evento' },
          { title: 'Decide', description: 'Razona con datos' },
          { title: 'Actúa', description: 'Materializa la acción' },
          { title: 'Audita', description: 'Cada paso, registrado' },
        ],
        callout: 'La única plataforma que **genera y despliega** agentes autónomos en el mismo flujo. Otros frameworks te dan el agente; WAI te lo deja en producción, con HTTPS, auditoría y rollback.',
        cta: 'Ver el video demo de 10 minutos',
        imageAlt: 'Aplicación de agente autónomo de WAI con vigilancia 24/7, registros de ejecución y trazabilidad',
      },
      demo: {
        title: 'Ve WAI funcionando, de principio a fin',
        subtitle: 'Un único video de 10 minutos: conectas datos, entiendes el dominio, construyes apps de negocio, modelos y agentes — y los despliegas. Todo en el mismo producto.',
        duration: '10 minutos · Demo real',
        videoTitle: 'WAI · Recorrido completo de plataforma',
      },
      taglineBanner: {
        text: 'WAI no es una plataforma de IA.',
        sub: 'Es la plataforma empresarial donde la IA es el motor.',
      },
      security: {
        title: 'Seguridad empresarial, por diseño',
        subtitle: 'Cuatro pilares — uno por pestaña. Aislamiento real, privilegios mínimos, identidad en cada capa y auditoría completa.',
        frameworksLine: 'Alineado con CIS Controls v8 · OWASP Top 10 · OWASP Top 10 LLM',
        tabs: {
          isolated: {
            label: 'Aislado',
            title: 'Tu propio entorno aislado',
            subtitle: 'Cada cliente, su caja. Datos, secretos, red, conocimiento y runtime — separados por diseño, no por convención.',
            bullets: [
              { title: 'Base de datos dedicada', description: 'Tus datos viven en su propio cluster, sin convivir con los de otros clientes.' },
              { title: 'Almacén de secretos propio', description: 'Tus credenciales no salen de tu tenant ni se comparten entre proyectos.' },
              { title: 'Dominio y red propios', description: 'URLs, certificados y políticas de red dedicados a tu organización.' },
              { title: 'Conocimiento aislado', description: 'Tu capa semántica nunca se mezcla con la de otra empresa.' },
              { title: 'Runtime de agentes dedicado', description: 'Cada agente corre en un entorno endurecido y aislado, sin acceso a vecinos.' },
              { title: 'Auditoría y coste por cliente', description: 'Cada acción y cada euro están imputados a tu tenant, no agregados al genérico.' },
            ],
          },
          readonly: {
            label: 'Sólo lectura',
            title: 'Cada salto, menos privilegios',
            subtitle: 'WAI conecta a tus datos en sólo lectura por defecto. Y en cada capa que atraviesa la información, los permisos bajan un escalón.',
            bullets: [
              { title: 'Conexión read-only por defecto', description: 'No hay caminos de escritura abiertos hacia tus sistemas; los habilitas tú, explícitamente.' },
              { title: 'Capa de acceso parametrizada', description: 'Las consultas pasan por una capa intermedia que valida, parametriza y limita.' },
              { title: 'Tools controladas, no SQL libre', description: 'El agente llama a herramientas definidas, no escribe SQL contra tu base de datos.' },
              { title: 'Privilegio mínimo al modelo', description: 'Al modelo IA llega sólo lo necesario para responder — nada más.' },
            ],
          },
          identity: {
            label: 'Identidad',
            title: 'Identidad real en cada capa',
            subtitle: 'Personas, no tokens compartidos. Cada acción se hace con la identidad real del usuario que la dispara, y un sandbox protege la ejecución.',
            bullets: [
              { title: 'SSO empresarial', description: 'Integración con tu proveedor de identidad. Sin contraseñas locales, sin cuentas técnicas paralelas.' },
              { title: 'ACL por usuario', description: 'Cada usuario ve sólo lo que tiene autorizado, dentro y fuera de los agentes.' },
              { title: 'Permisos tool-by-tool', description: 'Para cada herramienta del agente: allow, deny o ask. Granularidad real.' },
              { title: 'Una credencial, una puerta', description: 'No reutilizamos secretos entre componentes. Si una rota, no abre otra cosa.' },
            ],
          },
          audit: {
            label: 'Auditoría',
            title: 'Cada acción, registrada y exportable',
            subtitle: 'Quién hizo qué, cuándo, con qué datos y qué resultado. Versionado en todas las capas. Exportable a tu SIEM o sistema de observabilidad.',
            bullets: [
              { title: 'Quién, qué y cuándo', description: 'Cada llamada de cada agente queda registrada con usuario, hora y contexto.' },
              { title: 'Coste por agente y por usuario', description: 'Sabes lo que cuesta cada caso de uso, sin sorpresas en la factura.' },
              { title: 'Versionado con rollback', description: 'Apps de negocio, modelos y agentes versionados; vuelves atrás con un clic.' },
              { title: 'Exportable a tu SIEM', description: 'Streaming de eventos a tu pila de seguridad: nada queda encerrado en WAI.' },
            ],
          },
        },
        sandbox: {
          title: 'Sandbox, no portátil',
          laptop: 'Portátil del developer',
          wai: 'WAI sandbox',
          rows: [
            { label: 'Credenciales', laptop: 'En el llavero personal', wai: 'En el almacén de secretos del tenant' },
            { label: 'Acceso a archivos', laptop: 'A todo tu sistema', wai: 'Solo al volumen del agente' },
            { label: 'Red', laptop: 'Internet completo', wai: 'Egreso curado y allowlist' },
            { label: 'Radio de impacto', laptop: 'Cualquier proyecto', wai: 'Sólo el tenant del agente' },
          ],
        },
        flow: ['Tu base de datos', 'Capa de acceso WAI', 'Tools controladas', 'Agente / App', 'Modelo IA'],
        transports: ['TLS', 'Túnel SSH', 'VPN privada', 'IP allowlist'],
      },
      whywai: {
        title: 'Por qué WAI',
        subtitle: 'Cuatro razones que ya están operando en más de 25 empresas, en producción.',
        reasons: [
          {
            title: 'Tus datos se quedan donde están',
            description: 'Conectamos in-place. Sin lagos paralelos, sin movimientos masivos, sin proyectos de migración.',
          },
          {
            title: 'IA con sandbox, no con tu portátil',
            description: 'Cada agente corre aislado, con identidad propia y privilegios mínimos. No es un copiloto suelto.',
          },
          {
            title: 'Listo para auditoría',
            description: 'Quién, qué, cuándo y a qué coste — todo registrado y exportable a tu SIEM.',
          },
          {
            title: 'Genera y despliega, en uno',
            description: 'Apps de negocio, modelos y agentes — incluidos los autónomos — salen a producción dentro del mismo WAI.',
          },
        ],
        closing: 'WAI no es una plataforma de IA. Es la plataforma empresarial donde la IA es el motor.',
        cta: '+25 empresas ya operan con WAI en producción. ¿Te enseñamos cómo?',
        ctaButton: 'Hablar con el equipo',
      },
      resellers: {
        eyebrow: 'Red de partners',
        title: 'Partners autorizados, expertise local',
        subtitle: 'Trabajamos con partners especializados que llevan WAI a cada región: implementación certificada, asesoría ejecutiva y acompañamiento en el idioma y la operación del cliente.',
        partners: [
          {
            name: 'RDWAI',
            wordmark: 'RDWAI',
            tagline: 'AI Business Assistant para la alta dirección',
            region: 'México · LATAM · EE.UU. · España',
            description: 'Partner oficial de WAI para LATAM. RDWAI integra WAI en la operación ejecutiva del cliente, traduce los objetivos del negocio en lógica accionable y acompaña a la alta dirección en la toma de decisiones del día a día.',
            badges: ['Asesoría ejecutiva', 'Implementación certificada', 'Atención en español', 'Presencia LATAM'],
            url: 'https://rdwai.com/',
            cta: 'Visitar RDWAI',
          },
        ],
        becomePartner: {
          title: '¿Quieres convertirte en partner de WAI?',
          description: 'Buscamos integradores, consultoras estratégicas y firmas de transformación digital con presencia regional.',
          cta: 'Hablemos del programa de partners',
          email: 'info@waiworking.com',
        },
      },
      connectors: {
        title: 'Conectores de Datos Disponibles',
        subtitle: 'WAI se integra con más de 50 fuentes de datos mediante conectores seguros y confiables. Conecta tus sistemas existentes sin complicaciones.',
        viewConnectors: 'Ver Lista de Conectores',
        searchPlaceholder: 'Buscar conectores...',
        totalConnectors: 'conectores disponibles',
        buildOwn: '¿Necesitas construir tu propio conector?',
        contactUs: 'Contáctanos para conectores personalizados',
        contactSubject: 'Solicito un conector personalizado para WAI',
      },
      pricing: {
        eyebrow: 'Precios',
        title: 'Precios a medida, vía partners oficiales',
        subtitle: 'WAI se contrata a través de su red de partners certificados. Tu partner adapta el alcance funcional, los conectores activos y el modelo económico al contexto de tu organización —no aplicamos un único precio de catálogo para todas las empresas.',
        note: 'Cada despliegue de WAI es distinto: tamaño de la organización, fuentes de datos, sectores, SLAs y modelo de despliegue. El partner local te acompaña en el diseño del proyecto y en la propuesta económica.',
        includedTitle: 'Qué incluye un proyecto WAI',
        included: [
          'Entorno dedicado por cliente',
          'Conectividad in-place a tus fuentes (+50 conectores)',
          'Capa semántica automática sobre tus datos',
          'Generación de apps de negocio, modelos y agentes — incluidos autónomos',
          'Despliegue, HTTPS y versionado con rollback',
          'Seguridad empresarial y auditoría completa',
          'Onboarding y soporte por tu partner local',
        ],
        cardTitle: 'Habla con un partner oficial',
        cardSubtitle: 'Red de partners certificados — actualmente con presencia en Latinoamérica y Europa.',
        primaryCta: 'Ver partners oficiales',
        secondaryCta: 'Solicitar contacto general',
        contactSubject: 'Información comercial y de precios de WAI',
      },
      connectivityGuide: {
        eyebrow: 'Guía técnica · Mayo 2026',
        title: 'Cuatro opciones de conectividad. Tú eliges cómo nos conectamos a tus datos.',
        subtitle: 'Documento descriptivo para equipos de seguridad, sistemas y arquitectura. Describe el modelo de conectividad de WAI a tus bases de datos, las garantías comunes a todas las opciones y una comparativa capa por capa.',
        audience: 'Audiencia: equipos de seguridad, sistemas y arquitectura',
        confidential: 'Documento descriptivo, no prescriptivo',
        optionsTitle: 'Cuatro opciones, una elección',
        options: [
          { code: 'A', name: 'Base de datos pública en Internet', short: 'Endpoint público con TLS nativo del motor.', isolation: 'Aislamiento bajo' },
          { code: 'B', name: 'Pública con whitelist por IP', short: 'TLS + firewall del cliente restringido a la IP fija de salida de WAI.', isolation: 'Aislamiento medio' },
          { code: 'C', name: 'Túnel SSH a través de bastión', short: 'Base de datos sin exposición a Internet. Doble cifrado SSH + TLS.', isolation: 'Aislamiento medio-alto' },
          { code: 'D', name: 'Tailscale · Zero Trust (enterprise)', short: 'WireGuard end-to-end con identidad criptográfica por dispositivo. Sin puertos abiertos en tu firewall.', isolation: 'Aislamiento alto' },
        ],
        guaranteesTitle: 'Garantías comunes a todas las opciones',
        guarantees: [
          'Cifrado en tránsito (TLS o WireGuard)',
          'Mínimo privilegio: usuario aplicativo de sólo lectura',
          'Credenciales cifradas en reposo, en secrets manager',
          'Revocación inmediata por parte del cliente',
          'Auditoría completa, exportable a tu SIEM',
        ],
        downloadCta: 'Descargar guía (PDF)',
        downloadMeta: 'PDF · 15 páginas · ~325 KB',
        downloadHref: '/docs/wai-guia-conectividad-bases-de-datos.pdf',
        downloadAria: 'Descargar la guía técnica de conectividad de bases de datos en PDF',
      },
      footer: {
        company: 'WAI - Working AI',
        tagline: 'La plataforma empresarial donde la IA es el motor.',
        navigation: 'Navegación',
        legal: 'Legal y Soporte',
        privacyPolicy: 'Política de Privacidad',
        termsOfService: 'Términos de Servicio',
        contact: 'Contacto: info@waiworking.com',
        builtWith: 'Construido para el futuro de las empresas',
        copyright: '© 2026 WAI - Working AI. Todos los derechos reservados.',
      },
      routes: {
        backToOverview: '← Ver plataforma completa',
        fullPlatformCard: {
          eyebrow: 'Vista global',
          title: 'Mira la plataforma de extremo a extremo',
          description: 'De la conexión a tus datos al despliegue de agentes autónomos en producción. Todo en un solo recorrido.',
          cta: 'Ir al overview',
        },
        home: {
          title: 'WAI · La plataforma empresarial donde la IA es el motor',
          description: 'WAI conecta tus datos donde ya están, los entiende con IA y despliega apps de negocio, modelos predictivos y agentes autónomos — en un mismo producto, con seguridad y auditoría empresarial.',
        },
        platform: {
          title: 'Plataforma · De tus datos a aplicaciones IA para negocio · WAI',
          description: 'Conecta in-place, entiende con IA, construye apps de negocio, modelos y agentes, y despliega — todo en un solo producto. Conoce las cuatro capas de la plataforma WAI.',
          eyebrow: 'Plataforma',
          heroTitle: 'Una plataforma. De tus datos a aplicaciones IA para negocio, en vivo.',
          heroSubtitle: 'Cuatro capas — Conecta, Entiende, Construye y Despliega — en un solo producto. Sin saltar de herramienta, sin proyectos paralelos.',
        },
        autonomous: {
          title: 'Agentes Autónomos · Vigilan, deciden y actúan 24/7 · WAI',
          description: 'Agentes autónomos empresariales: vigilancia continua, decisiones auditadas y despliegue en el mismo producto. La capacidad diferencial de WAI.',
          eyebrow: 'Capacidad diferencial',
          heroTitle: 'Agentes que siguen trabajando cuando tú te desconectas.',
          heroSubtitle: 'Vigilancia 24/7 de tu negocio, detección de eventos, decisiones razonadas y acción — todo registrado y desplegado como el resto de WAI.',
        },
        security: {
          title: 'Seguridad empresarial · Aislamiento, sólo lectura, identidad, auditoría · WAI',
          description: 'Seguridad por diseño: entorno aislado por cliente, sólo lectura por defecto, identidad real en cada capa y auditoría completa exportable a tu SIEM.',
          eyebrow: 'Seguridad',
          heroTitle: 'Seguridad empresarial, por diseño.',
          heroSubtitle: 'Cuatro pilares en producción: aislamiento real, mínimo privilegio, identidad en cada capa y auditoría completa. Alineado con CIS Controls v8 y OWASP Top 10 LLM.',
        },
        partners: {
          title: 'Partners · Red de implantación regional · WAI',
          description: 'Partners autorizados de WAI con experiencia local. Implantación certificada, asesoría ejecutiva y atención en el idioma y modelo operativo del cliente.',
          eyebrow: 'Red de partners',
          heroTitle: 'Partners autorizados, presencia regional.',
          heroSubtitle: 'Trabajamos con integradores y consultoras estratégicas que llevan WAI a cada región — con implantación certificada y acompañamiento ejecutivo.',
        },
        demo: {
          title: 'Demo · Recorrido end-to-end por la plataforma · WAI',
          description: 'Demo de 10 minutos por toda la plataforma WAI: conexión de datos, capa semántica, generación de apps de negocio, modelos y agentes, y despliegue en producción.',
          eyebrow: 'Demo en vivo',
          heroTitle: 'Mira WAI funcionando, de extremo a extremo.',
          heroSubtitle: '10 minutos: conexión de datos, entendimiento con IA, generación de apps de negocio, modelos y agentes — y despliegue en un mismo producto.',
        },
        connectors: {
          title: 'Conectores · +50 fuentes empresariales · WAI',
          description: 'Más de 50 conectores empresariales listos: ERPs, CRMs, data warehouses, ficheros y APIs. Conecta tus sistemas existentes in-place, sin movimientos forzados.',
          eyebrow: 'Catálogo',
          heroTitle: 'Conecta tus datos donde ya están.',
          heroSubtitle: 'Más de 50 conectores empresariales — ERPs, CRMs, data warehouses, ficheros y APIs — con permisos respetados y sólo lectura por defecto.',
        },
        pricing: {
          title: 'Precios WAI · Vía partners oficiales · WAI',
          description: 'WAI se contrata a través de partners oficiales que adaptan el alcance, los conectores y el modelo económico al contexto de cada empresa. No hay precio único de catálogo.',
          eyebrow: 'Modelo de contratación',
          heroTitle: 'Precios a medida, vía partners oficiales.',
          heroSubtitle: 'Sin precio único de catálogo. Tu partner certificado adapta el alcance y la propuesta económica a tu organización: tamaño, sectores, SLAs y modelo de despliegue.',
        },
      },
    },
  },
  en: {
    translation: {
      nav: {
        home: 'Home',
        customers: 'Customers',
        platform: 'Platform',
        autonomous: 'Autonomous Agents',
        connectors: 'Connectors',
        demo: 'Demo',
        security: 'Security',
        partners: 'Partners',
        pricing: 'Pricing',
      },
      tagline: {
        differentiator: "WAI isn't an AI platform. It's the enterprise platform that runs on AI.",
        short: 'Enterprise platform. AI engine.',
      },
      hero: {
        title: 'Working AI',
        subtitle: "WAI isn't an AI platform. It's the enterprise platform that runs on AI.",
        description: 'We connect your data where it already lives, learn what it means with AI, and build the business apps, predictive models and agents — autonomous ones included — your enterprise needs. And we deploy them inside the same product.',
        primaryCta: 'Watch live demo',
        secondaryCta: 'Explore the platform',
        pillarConnect: 'Connect',
        pillarUnderstand: 'Understand',
        pillarBuild: 'Build',
        pillarDeploy: 'Deploy',
        imageAlt: 'Operational health dashboard generated by WAI, with live KPIs on enterprise data',
      },
      customers: {
        eyebrow: 'Proven in production',
        number: '25+',
        title: 'companies run WAI every day',
        subtitle: 'Each one runs WAI as its enterprise engine —not as one more AI tool—, on its own data. Critical workflows, real decisions, live in production.',
        credibility: ['Live in production', 'Multi-sector', 'Multi-country', 'Regulated environments'],
        reinforcement: "Because WAI isn't an AI platform: it's the enterprise platform that runs on AI.",
      },
      oneplatform: {
        title: 'One platform. From your data to live AI apps for business.',
        subtitle: 'Four steps. One product. Security and governance, in every layer.',
        closing: 'Same product, same flow — no hopping between tools.',
        steps: [
          {
            title: 'Connect',
            description: 'To your data where it already lives. No unnecessary copies, no forced moves.',
          },
          {
            title: 'Understand',
            description: 'WAI reads your data and learns what it means. No manual dictionary.',
          },
          {
            title: 'Build',
            description: 'Business apps, predictive models and custom agents — autonomous ones included.',
          },
          {
            title: 'Deploy',
            description: 'One click and live. Same product, public URL, versioned.',
          },
        ],
      },
      connect: {
        eyebrow: '01 · Connect',
        title: 'Connect where your data already lives',
        subtitle: 'WAI plugs in-place to your enterprise systems. You choose the sources and the tables; we respect your permissions and work read-only by default.',
        tagline: 'Your data, where it already lives — WAI connects in-place, no unnecessary copies.',
        bullets: [
          {
            title: 'Curated connector catalog',
            description: 'Over 50 enterprise-ready connectors: ERPs, CRMs, data warehouses, files and APIs. Your sources, not ours.',
          },
          {
            title: 'Dedicated environment per customer',
            description: 'Each customer has their own box: data, secrets and network isolated, outside any other tenant.',
          },
          {
            title: 'Read-only by default',
            description: 'No open write paths. Privileges drop at every hop: only the strict minimum reaches the model.',
          },
        ],
        imageCaption: 'New connection wizard with an enterprise catalog of sources.',
        imageAlt: 'WAI assistant for creating an in-place data connection to enterprise systems',
      },
      understand: {
        eyebrow: '02 · Understand',
        title: 'WAI reads your data and learns what it means',
        subtitle: 'No manual dictionary. No parallel governance project. The knowledge layer builds itself as you connect your sources.',
        tagline: 'Your knowledge layer, automatic.',
        bullets: [
          {
            title: 'Tables, columns and documents together',
            description: 'WAI unifies structured data with internal documentation — everything becomes searchable and understandable by the AI.',
          },
          {
            title: 'Indexed and governed per customer',
            description: 'Your knowledge lives in your tenant. It never mixes with another organization.',
          },
          {
            title: 'Gap detection',
            description: 'WAI flags missing context: columns without description, unexpected values, relationships to learn.',
          },
        ],
        imageCaption: 'Available knowledge view: semantic names, data classification and detected gaps.',
        imageAlt: "WAI's knowledge layer: tables, columns and documents classified with semantic names",
      },
      build: {
        eyebrow: '03 · Build',
        title: 'WAI builds with you: business apps, models and agents',
        subtitle: 'One creation layer, three deliverables. All versioned, all audited, all deployed inside WAI.',
        footnote: 'And it all ships inside the same product — see Deployment ↓',
        apps: {
          title: 'Custom business applications',
          description: 'Any business application over your data: dashboards, operational tools, process frontends, internal portals or management panels.',
          bullets: [
            'Any business application, not just analytics',
            'Reusable block library',
            'Verified: compile first time',
          ],
          alt: 'Business application generated by WAI over customer data, with bespoke UI and logic',
        },
        models: {
          title: 'Predictive models',
          description: 'Classification, regression and forecast. Without managing infrastructure.',
          bullets: [
            'WAI picks the right pattern',
            'Real metrics on real test sets',
            'Versioned with Live · Current · rollback',
          ],
          alt: 'WAI predictive model with real metrics on the test set',
        },
        agents: {
          title: 'Custom agents',
          description: 'Define the persona, the skills and the permissions. WAI handles the rest.',
          bullets: [
            'System prompt, instructions and MCP Servers',
            'Tool-by-tool permissions: allow · deny · ask',
            'Versioned, with full history',
          ],
          alt: 'Definition of a WAI agent: persona, instructions and per-tool permissions',
        },
      },
      deployment: {
        eyebrow: '04 · Deploy',
        title: 'One click. Public URL. Done.',
        subtitle: 'Your business apps, models and agents ship to production inside the same WAI — no separate DevOps team, no second platform.',
        sameProductTagline: "WAI doesn't stop at generating: it ships.",
        chips: [
          'Production-ready',
          'Automatic HTTPS',
          'Versioned with rollback',
        ],
        flow: [
          'Build in WAI',
          'Automatic deployment',
          'Public URL with HTTPS',
        ],
        browser: {
          url: 'app.your-tenant.wai.run',
          statusLabel: 'Status',
          statusValue: 'Live',
          versionLabel: 'Version',
          versionValue: 'v1.4 · shipped 2 min ago',
          footerChips: ['HTTPS', 'Versioned', '1-click rollback'],
        },
      },
      autonomous: {
        eyebrow: 'Differentiating capability',
        heroTitle: 'Agents that keep working when you log off.',
        heroSubtitle: 'Watching your business 24/7, detecting events, deciding and acting — with structured, audited responses. Deployed like the rest of your WAI.',
        bullets: [
          {
            title: '24/7 continuous watch',
            description: 'No human required. WAI watches your signals at the cadence you set.',
          },
          {
            title: 'Structured response',
            description: 'Every agent action is recorded with data, metrics and reasoning.',
          },
          {
            title: 'Hibernates when idle',
            description: 'It only consumes when there is something to process. No silent fixed cost.',
          },
          {
            title: 'Identity per deployment',
            description: 'Every agent acts with its own identity and permissions. No shortcuts, no shared secrets.',
          },
        ],
        cycleTitle: 'The autonomous cycle',
        cycleSteps: [
          { title: 'Watch', description: 'Listens to your signals' },
          { title: 'Detect', description: 'Identifies the event' },
          { title: 'Decide', description: 'Reasons with data' },
          { title: 'Act', description: 'Materializes the action' },
          { title: 'Audit', description: 'Every step, logged' },
        ],
        callout: 'The only platform that **generates and deploys** autonomous agents in a single flow. Other frameworks hand you the agent; WAI ships it to production, with HTTPS, audit and rollback.',
        cta: 'Watch the 10-minute demo',
        imageAlt: 'WAI autonomous agent application with 24/7 watch, execution logs and full traceability',
      },
      demo: {
        title: 'See WAI running, end to end',
        subtitle: 'One 10-minute video: you connect data, you understand the domain, you build business apps, models and agents — and deploy them. All inside the same product.',
        duration: '10 minutes · Live demo',
        videoTitle: 'WAI · End-to-end platform walkthrough',
      },
      taglineBanner: {
        text: "WAI isn't an AI platform.",
        sub: "It's the enterprise platform that runs on AI.",
      },
      security: {
        title: 'Enterprise security, by design',
        subtitle: 'Four pillars — one per tab. Real isolation, least privilege, identity at every layer and complete audit.',
        frameworksLine: 'Aligned with CIS Controls v8 · OWASP Top 10 · OWASP Top 10 LLM',
        tabs: {
          isolated: {
            label: 'Isolated',
            title: 'Your own isolated environment',
            subtitle: 'Each customer, its own box. Data, secrets, network, knowledge and runtime — separated by design, not by convention.',
            bullets: [
              { title: 'Dedicated database', description: 'Your data lives in its own cluster, never colocated with other customers.' },
              { title: 'Own secret store', description: 'Your credentials never leave your tenant or get shared across projects.' },
              { title: 'Own domain and network', description: 'URLs, certificates and network policies dedicated to your organization.' },
              { title: 'Isolated knowledge', description: 'Your semantic layer never mixes with another organization.' },
              { title: 'Dedicated agent runtime', description: 'Every agent runs in a hardened, isolated environment, with no access to neighbors.' },
              { title: 'Audit and cost per customer', description: 'Every action and every euro is attributed to your tenant, never bundled into a global pool.' },
            ],
          },
          readonly: {
            label: 'Read-only',
            title: 'Every hop, less privilege',
            subtitle: 'WAI connects to your data read-only by default. And at every layer the data traverses, permissions go down one step.',
            bullets: [
              { title: 'Read-only connection by default', description: 'No open write paths back to your systems; you enable them yourself, explicitly.' },
              { title: 'Parameterized access layer', description: 'Queries go through an intermediate layer that validates, parameterizes and bounds them.' },
              { title: 'Controlled tools, not free SQL', description: 'The agent calls defined tools, not free SQL against your database.' },
              { title: 'Least privilege to the model', description: 'Only what is needed to answer reaches the AI model — nothing more.' },
            ],
          },
          identity: {
            label: 'Identity',
            title: 'Real identity at every layer',
            subtitle: 'People, not shared tokens. Every action is executed with the real identity of the user that triggered it, and a sandbox protects the execution.',
            bullets: [
              { title: 'Enterprise SSO', description: 'Integration with your identity provider. No local passwords, no parallel service accounts.' },
              { title: 'Per-user ACL', description: 'Each user sees only what they are authorized to, inside and outside the agents.' },
              { title: 'Tool-by-tool permissions', description: 'For every tool an agent uses: allow, deny or ask. Real granularity.' },
              { title: 'One credential, one door', description: "We don't reuse secrets across components. If one rotates, it doesn't open another lock.",
              },
            ],
          },
          audit: {
            label: 'Audit',
            title: 'Every action, logged and exportable',
            subtitle: 'Who did what, when, with what data and to what result. Versioned across every layer. Exportable to your SIEM or observability stack.',
            bullets: [
              { title: 'Who, what and when', description: 'Every agent call is recorded with user, time and context.' },
              { title: 'Cost per agent and per user', description: 'You know what each use case costs, no billing surprises.' },
              { title: 'Versioning with rollback', description: 'Business apps, models and agents are versioned; one click to roll back.' },
              { title: 'Exportable to your SIEM', description: 'Event streaming to your security stack: nothing stays locked inside WAI.' },
            ],
          },
        },
        sandbox: {
          title: 'Sandbox, not laptop',
          laptop: 'Developer laptop',
          wai: 'WAI sandbox',
          rows: [
            { label: 'Credentials', laptop: 'In your personal keychain', wai: 'In your tenant secret store' },
            { label: 'File access', laptop: 'Your whole system', wai: 'Only the agent volume' },
            { label: 'Network', laptop: 'Full internet', wai: 'Curated egress and allowlist' },
            { label: 'Blast radius', laptop: 'Any project', wai: 'Only the agent tenant' },
          ],
        },
        flow: ['Your database', 'WAI access layer', 'Controlled tools', 'Agent / App', 'AI model'],
        transports: ['TLS', 'SSH tunnel', 'Private VPN', 'IP allowlist'],
      },
      whywai: {
        title: 'Why WAI',
        subtitle: 'Four reasons already running in 25+ enterprises, in production.',
        reasons: [
          {
            title: 'Your data stays put',
            description: "We connect in-place. No parallel lakes, no massive moves, no migration projects.",
          },
          {
            title: 'Sandboxed AI, not your laptop',
            description: 'Every agent runs isolated, with its own identity and least privilege. Not a loose copilot.',
          },
          {
            title: 'Audit-ready',
            description: 'Who, what, when and at what cost — all logged and exportable to your SIEM.',
          },
          {
            title: 'Generate and deploy, in one',
            description: 'Business apps, models and agents — autonomous ones included — ship to production inside the same WAI.',
          },
        ],
        closing: "WAI isn't an AI platform. It's the enterprise platform that runs on AI.",
        cta: "25+ enterprises already run on WAI in production. Want to see how?",
        ctaButton: 'Talk to the team',
      },
      resellers: {
        eyebrow: 'Partner network',
        title: 'Authorized partners, local expertise',
        subtitle: 'We work with specialized partners that bring WAI to every region: certified implementation, executive advisory and engagement in the customer\u2019s language and operating model.',
        partners: [
          {
            name: 'RDWAI',
            wordmark: 'RDWAI',
            tagline: 'AI Business Assistant for senior leadership',
            region: 'Mexico · LATAM · USA · Spain',
            description: 'Official WAI partner for LATAM. RDWAI embeds WAI into the customer\u2019s executive operations, translates business objectives into actionable logic and accompanies senior leadership through day-to-day decision-making.',
            badges: ['Executive advisory', 'Certified implementation', 'Spanish-speaking', 'LATAM presence'],
            url: 'https://rdwai.com/',
            cta: 'Visit RDWAI',
          },
        ],
        becomePartner: {
          title: 'Want to become a WAI partner?',
          description: 'We are looking for integrators, strategic consultancies and digital-transformation firms with regional presence.',
          cta: 'Talk about the partner program',
          email: 'info@waiworking.com',
        },
      },
      connectors: {
        title: 'Available Data Connectors',
        subtitle: 'WAI integrates with over 50 data sources through secure and reliable connectors. Connect your existing systems without complications.',
        viewConnectors: 'View Connectors List',
        searchPlaceholder: 'Search connectors...',
        totalConnectors: 'available connectors',
        buildOwn: 'Need to build your own connector?',
        contactUs: 'Contact us for custom connectors',
        contactSubject: 'Requesting a custom connector for WAI',
      },
      pricing: {
        eyebrow: 'Pricing',
        title: 'Custom pricing, through official partners',
        subtitle: "WAI is contracted through its network of certified partners. Your partner adapts the functional scope, active connectors and commercial model to your organization's context — we don't apply a single catalogue price to every company.",
        note: "Every WAI rollout is different: organization size, data sources, sectors, SLAs and deployment model. Your local partner walks you through scoping and the commercial proposal.",
        includedTitle: 'What a WAI project includes',
        included: [
          'Dedicated environment per customer',
          'In-place connectivity to your sources (50+ connectors)',
          'Automatic semantic layer on your data',
          'Generation of business apps, models and agents — autonomous ones included',
          'Deployment, HTTPS and versioning with rollback',
          'Enterprise security and full audit',
          'Onboarding and support by your local partner',
        ],
        cardTitle: 'Talk to an official partner',
        cardSubtitle: 'Network of certified partners — currently with presence in Latin America and Europe.',
        primaryCta: 'See official partners',
        secondaryCta: 'Request general contact',
        contactSubject: 'WAI commercial and pricing information',
      },
      connectivityGuide: {
        eyebrow: 'Technical guide · May 2026',
        title: 'Four connectivity options. You choose how we connect to your data.',
        subtitle: "A descriptive document for security, systems and architecture teams. Covers WAI's connectivity model to your databases, the guarantees common to every option and a layer-by-layer comparison.",
        audience: 'Audience: security, systems and architecture teams',
        confidential: 'Descriptive document, not prescriptive',
        optionsTitle: 'Four options, one choice',
        options: [
          { code: 'A', name: 'Database public on the Internet', short: 'Public endpoint with the database engine\u2019s native TLS.', isolation: 'Low isolation' },
          { code: 'B', name: 'Public with IP allowlist', short: "TLS + customer firewall restricted to WAI's fixed egress IP.", isolation: 'Medium isolation' },
          { code: 'C', name: 'SSH tunnel via bastion host', short: 'Database never exposed to the Internet. Double encryption: SSH + TLS.', isolation: 'Medium-high isolation' },
          { code: 'D', name: 'Tailscale · Zero Trust (enterprise)', short: 'End-to-end WireGuard with per-device cryptographic identity. No ports opened in your firewall.', isolation: 'High isolation' },
        ],
        guaranteesTitle: 'Guarantees common to every option',
        guarantees: [
          'In-transit encryption (TLS or WireGuard)',
          'Least privilege: read-only application user',
          'Credentials encrypted at rest, in a secret manager',
          'Immediate revocation by the customer',
          'Full audit, exportable to your SIEM',
        ],
        downloadCta: 'Download the guide (PDF)',
        downloadMeta: 'PDF · 15 pages · ~325 KB',
        downloadHref: '/docs/wai-guia-conectividad-bases-de-datos.pdf',
        downloadAria: 'Download the database connectivity technical guide as PDF',
      },
      footer: {
        company: 'WAI - Working AI',
        tagline: 'The enterprise platform that runs on AI.',
        navigation: 'Navigation',
        legal: 'Legal & Support',
        privacyPolicy: 'Privacy Policy',
        termsOfService: 'Terms of Service',
        contact: 'Contact: info@waiworking.com',
        builtWith: 'Built for the future of enterprises',
        copyright: '© 2026 WAI - Working AI. All rights reserved.',
      },
      routes: {
        backToOverview: '← See the full platform',
        fullPlatformCard: {
          eyebrow: 'Big picture',
          title: 'See the platform end to end',
          description: 'From connecting your data to deploying autonomous agents in production. The full journey, in one place.',
          cta: 'Go to the overview',
        },
        home: {
          title: 'WAI · The enterprise platform that runs on AI',
          description: 'WAI connects your data where it already lives, understands it with AI and ships business apps, predictive models and autonomous agents — in a single product, with enterprise security and audit.',
        },
        platform: {
          title: 'Platform · From your data to live AI applications for business · WAI',
          description: 'Connect in-place, understand with AI, build business apps, models and agents, and deploy — all in one product. Explore the four layers of the WAI platform.',
          eyebrow: 'Platform',
          heroTitle: 'One platform. From your data to live AI applications for business.',
          heroSubtitle: 'Four layers — Connect, Understand, Build and Deploy — in a single product. No tool hopping, no parallel projects.',
        },
        autonomous: {
          title: 'Autonomous Agents · Watch, decide and act 24/7 · WAI',
          description: 'Enterprise-grade autonomous agents: continuous watch, audited decisions and deployment in the same product. The differentiating capability of WAI.',
          eyebrow: 'Differentiating capability',
          heroTitle: 'Agents that keep working when you log off.',
          heroSubtitle: '24/7 watch on your business, event detection, reasoned decisions and action — all recorded and deployed like the rest of WAI.',
        },
        security: {
          title: 'Enterprise security · Isolation, read-only, identity, audit · WAI',
          description: 'Security by design: isolated tenant per customer, read-only by default, real identity at every layer and full audit, exportable to your SIEM.',
          eyebrow: 'Security',
          heroTitle: 'Enterprise security, by design.',
          heroSubtitle: 'Four pillars in production: real isolation, least privilege, identity at every layer and full audit. Aligned with CIS Controls v8 and OWASP Top 10 LLM.',
        },
        partners: {
          title: 'Partners · Regional implementation network · WAI',
          description: 'Authorized WAI partners with local expertise. Certified implementation, executive advisory and engagement in the customer\u2019s language and operating model.',
          eyebrow: 'Partner network',
          heroTitle: 'Authorized partners, regional reach.',
          heroSubtitle: 'We work with integrators and strategic consultancies that bring WAI to every region — with certified implementation and executive engagement.',
        },
        demo: {
          title: 'Demo · End-to-end platform walkthrough · WAI',
          description: 'A 10-minute demo of the entire WAI platform: data connection, semantic layer, generation of business apps, models and agents, and deployment to production.',
          eyebrow: 'Live demo',
          heroTitle: 'See WAI running, end to end.',
          heroSubtitle: '10 minutes: data connection, AI understanding, generation of business apps, models and agents — and deployment, in the same product.',
        },
        connectors: {
          title: 'Connectors · 50+ enterprise sources · WAI',
          description: 'Over 50 enterprise-ready connectors: ERPs, CRMs, data warehouses, files and APIs. Plug into your existing systems in-place, no forced moves.',
          eyebrow: 'Catalog',
          heroTitle: 'Connect to your data where it already lives.',
          heroSubtitle: 'More than 50 enterprise connectors — ERPs, CRMs, data warehouses, files and APIs — with permissions respected and read-only by default.',
        },
        pricing: {
          title: 'WAI Pricing · Through official partners · WAI',
          description: "WAI is contracted through official partners that adapt scope, connectors and commercial model to each company's context. There is no single catalogue price.",
          eyebrow: 'Commercial model',
          heroTitle: 'Custom pricing, through official partners.',
          heroSubtitle: "No single catalogue price. Your certified partner adapts the scope and the commercial proposal to your organization: size, sectors, SLAs and deployment model.",
        },
      },
    },
  },
};
